import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Image as ImgIcon, AlertTriangle, CheckCircle2, Hash, Sparkles, Instagram, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { proxyImage } from "@/lib/img";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PriorityBadge({ priority }) {
  const map = {
    hoch: "bg-red-500/15 text-red-400 border-red-500/30",
    mittel: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    niedrig: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${map[priority] || map.mittel}`}>{priority}</span>;
}

function ScoreBubble({ score, label }) {
  const color = score >= 70 ? "text-emerald-400 bg-emerald-500/10" : score >= 40 ? "text-amber-400 bg-amber-500/10" : "text-red-400 bg-red-500/10";
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${color}`}>
      <span className="text-2xl font-bold">{score}</span>
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </div>
  );
}

export default function PostCheckPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlatform = searchParams.get("platform") === "tiktok" ? "tiktok" : "instagram";
  const [platform, setPlatform] = useState(initialPlatform);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  useEffect(() => {
    const p = searchParams.get("platform");
    if (p === "tiktok" || p === "instagram") setPlatform(p);
  }, [searchParams]);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/analyse/post-check`, { url: url.trim() });
      setResult(data);
      toast.success("Post-Analyse fertig!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post-Check fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const ai = result?.ai_analysis || {};
  const post = result?.post_data || {};

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <ImgIcon className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{platform === "tiktok" ? "Video-Check" : "Post-/Reel-Check"}</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{platform}</span>
        </div>

        {!result && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}
            {/* Platform Switch */}
            <div className="flex gap-2" data-testid="platform-switch">
              {[
                { id: "instagram", label: "Instagram", icon: Instagram },
                { id: "tiktok", label: "TikTok", icon: Zap },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-medium transition-all ${
                    platform === p.id ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                  data-testid={`platform-${p.id}`}
                >
                  <p.icon className="w-3.5 h-3.5" />
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-zinc-400">
              {platform === "tiktok"
                ? "Füge die URL eines TikTok-Videos ein — wir analysieren Hook, Caption, Hashtags und Performance."
                : "Füge die URL eines Instagram-Posts oder Reels (Image, Carousel, Video) ein — wir bewerten Caption, Hashtags und liefern dir konkrete Verbesserungs-Vorschläge."}
            </p>
            <Input
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && run()}
              placeholder={platform === "tiktok" ? "https://www.tiktok.com/@user/video/..." : "https://www.instagram.com/p/... oder /reel/..."}
              className="bg-zinc-900 border-zinc-800 text-white h-11"
              data-testid="post-url-input"
            />
            <Button onClick={run} disabled={loading || !url.trim()} className="w-full bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white h-11" data-testid="run-post-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird analysiert...</> : <><Sparkles className="w-4 h-4 mr-2" />Analysieren</>}
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="post-result">
            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 flex items-start gap-4">
              {post.thumbnail && <img src={proxyImage(post.thumbnail)} alt="" className="w-20 h-20 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-semibold">@{post.owner_username}</p>
                <p className="text-xs text-zinc-400 line-clamp-3 mt-1">{post.caption}</p>
                <div className="flex gap-3 mt-2 text-xs text-zinc-500">
                  <span><strong className="text-white">{post.likes?.toLocaleString()}</strong> Likes</span>
                  <span><strong className="text-white">{post.comments?.toLocaleString()}</strong> Comments</span>
                  {post.views > 0 && <span><strong className="text-white">{post.views?.toLocaleString()}</strong> Views</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <ScoreBubble score={ai.overall_score || 0} label="Gesamt" />
              <span className="text-xs uppercase tracking-wider px-2 py-1 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">{ai.performance_level}</span>
            </div>
            <p className="text-sm text-zinc-300 p-4 rounded-xl bg-zinc-900 border border-zinc-800">{ai.verdict}</p>

            {ai.caption_analysis && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-semibold text-white">Caption-Analyse</p>
                  <ScoreBubble score={ai.caption_analysis.score || 0} label="Score" />
                </div>
                {ai.caption_analysis.structure_critique && <p className="text-xs text-zinc-300 mb-2">{ai.caption_analysis.structure_critique}</p>}
                {ai.caption_analysis.alternative_hooks?.length > 0 && (
                  <><p className="text-xs text-cyan-400 mb-1">Alternative Hooks:</p>
                  <ul className="text-xs text-zinc-300 space-y-0.5 mb-2">{ai.caption_analysis.alternative_hooks.map((h, i) => <li key={i}>▸ {h}</li>)}</ul></>
                )}
                {ai.caption_analysis.rewritten_caption && (
                  <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-1">Optimierte Caption:</p>
                    <p className="text-xs text-cyan-300 whitespace-pre-wrap">{ai.caption_analysis.rewritten_caption}</p>
                  </div>
                )}
              </div>
            )}

            {ai.hashtag_analysis && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">Hashtags</p>
                  <ScoreBubble score={ai.hashtag_analysis.score || 0} label="Score" />
                </div>
                {ai.hashtag_analysis.recommended_top5?.length > 0 && (
                  <><p className="text-xs text-emerald-400 mb-1">Top 5:</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">{ai.hashtag_analysis.recommended_top5.map((h, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400">#{h.replace('#','')}</span>)}</div></>
                )}
                {ai.hashtag_analysis.to_remove?.length > 0 && (
                  <><p className="text-xs text-red-400 mb-1">Vermeiden:</p>
                  <div className="flex flex-wrap gap-1.5">{ai.hashtag_analysis.to_remove.map((h, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-red-500/10 text-red-400 line-through">#{h.replace('#','')}</span>)}</div></>
                )}
              </div>
            )}

            {(ai.compliance_check?.engagement_bait_detected || ai.compliance_check?.issues_found?.length > 0) && (
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <p className="text-sm font-semibold text-amber-300">Compliance-Findings</p>
                </div>
                {ai.compliance_check.issues_found?.length > 0 && <ul className="text-xs text-amber-200/80 space-y-1">{ai.compliance_check.issues_found.map((i, idx) => <li key={idx}>▸ {i}</li>)}</ul>}
              </div>
            )}

            {ai.improvement_checklist?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">Verbesserungs-Checkliste</p>
                </div>
                <ul className="space-y-2">
                  {ai.improvement_checklist.map((c, i) => (
                    <li key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                      <div className="flex items-center gap-2 mb-1"><PriorityBadge priority={c.priority} /></div>
                      <p className="text-xs text-zinc-200">{c.task}</p>
                      {c.impact && <p className="text-[10px] text-zinc-500 mt-0.5">→ {c.impact}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={() => { setResult(null); setUrl(""); }} variant="outline" className="w-full border-zinc-700 text-zinc-400">Anderen Post analysieren</Button>
          </div>
        )}
      </div>
    </div>
  );
}
